/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import Avatar from 'react-avatar';
import Loader from 'react-loader-spinner';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import slugify from 'react-slugify';
import { openDialog } from '../../../../app/dialog/dialog.actions';
import { finishMeetingSession } from '../../../../app/page/page-storage/meetings-simple/meetings-simple.actions';
import { meetingSimpleSelectors } from '../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { sessionSelectors } from '../../../../app/resource/session/session.selectors';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { ILiveMeetingNavbarProps } from './types';
import { hasUserElevatedPrivilegesDuringSession } from '../../../../utils/user.utils';
import { CircularAvatar } from '../../common/CustomAvatar';
import { Support } from '../../common/navigation-bars/NavigationBar/Support';
import { useTenantBranding } from '../../../../utils/custom-hooks/useTenantBranding.utils';

export const LiveMeetingNavbar: FunctionComponent<ILiveMeetingNavbarProps> = ({
  agendaItem,
  showLoader,
  meetingId,
  sessionId,
}) => {
  const history = useHistory();
  const session = useSelectWithParams(sessionSelectors.selectResourceById, sessionId);
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId ?? '');
  const { iconLogo, tenantName } = useTenantBranding();

  const [seconds, setSeconds] = useState<number>(0);
  const timer = useRef<NodeJS.Timeout | null>();
  const isTimeOut = useRef(false);

  useEffect(() => {
    if (timer.current) {
      clearInterval(timer.current);
      isTimeOut.current = false;
      timer.current = null;
    }

    // assume the value from session comes in UTC, so take it as such
    const lastStepStartedAt = moment.utc(session?.lastStepStartedAt);
    const secondsElapsed = moment().diff(lastStepStartedAt, 'seconds');

    typeof secondsElapsed !== 'number'
      ? setSeconds(+agendaItem.duration * 60)
      : setSeconds(+agendaItem.duration * 60 - secondsElapsed);
  }, [agendaItem.duration, session]);

  useEffect(() => {
    if (timer.current) {
      clearInterval(timer.current);
    }

    timer.current = setInterval(
      () =>
        setSeconds((sec) => {
          if (sec <= 0) {
            isTimeOut.current = true;
          }
          return sec - 1;
        }),
      1000,
    );
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [agendaItem, session]);
  const dispatch = useDispatch();
  const title = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId)?.title;
  const cancelMeetingSession = useCallback(() => {
    dispatch(finishMeetingSession(meetingId, sessionId, true));
    history.push(`/meeting-dashboard/${meetingId}/${slugify(title, { delimiter: '-' })}`);
  }, [dispatch, history, meetingId, sessionId, title]);

  const openAgendaFormDialog = useCallback(() => {
    dispatch(openDialog('aiPreviewDialog', { meetingId }));
  }, [dispatch, meetingId]);

  const openMeetingAttendeesDialog = useCallback(() => {
    dispatch(openDialog('meetingAttendeesDialog', { sessionId, meetingId }));
  }, [dispatch, meetingId, sessionId]);

  const [isActive, setActive] = useState(false);

  const toggleClass = () => {
    setActive(!isActive);
  };
  const close = useCallback(() => setActive(false), [setActive]);

  if (loggedUserId === null) {
    return null;
  }

  const hasElevatedPrivileges = hasUserElevatedPrivilegesDuringSession(loggedUserId, session, meeting);

  return (
    <div className="row mb-2" style={{ position: 'relative' }}>
      {/* Div bellow acts as a container for scrolling to top whenever we go to next agenda item */}
      <div style={{ position: 'absolute', top: '-30px' }} id="scrollContainer" />
      <div className="col-12">
        <div className="panel single-line flex aic jcsb p-0">
          <h2 className="bold flex aic">
            <a className="big-avatar flex aic jcc f-3 my-1 mx-2 white bold">
              {showLoader ? (
                <Loader type="TailSpin" color="var(--theme-primary-color)" height={40} width={40} />
              ) : meeting ? (
                <CircularAvatar
                  src={meeting.image}
                  title={meeting.title}
                  color={meeting.color}
                  size="60"
                  classes="bold"
                  textSizeRatio={2.5}
                  styles={{ margin: '10px 20px' }}
                />
              ) : (
                // if, for some reason, the meeting object is null then display the avatar for the step
                <Avatar
                  className="big-avatar yellow-bg flex aic jcc f-3 my-1 mx-2 white bold"
                  name={agendaItem.title}
                  round={true}
                  size="60"
                />
              )}
            </a>
            {agendaItem.title}
            <span className="badge thin green-bg white f-0 ml-05">LIVE</span>
          </h2>
          <span
            className="meeting-live-time"
            style={{ marginLeft: '32%', backgroundColor: isTimeOut.current ? 'red' : undefined }}
          >
            <i className="icon-clock mr-05"></i>{' '}
            {isTimeOut.current
              ? `-${moment.utc(Math.abs(seconds) * 1000).format('mm:ss')}`
              : `${moment.utc(seconds * 1000).format('mm:ss')}`}
          </span>
          <ul className="right-menu" style={{ height: '80px' }}>
            <li className="panel-text ml-2">
              <CircularAvatar src={iconLogo} title={tenantName} size="30" />
            </li>
            <li className="panel-text ml-2 pr-2">{tenantName}</li>
            <li className="flex aic">
              {hasElevatedPrivileges && (
                <div className={isActive ? 'dropdown opened' : 'dropdown'} onMouseLeave={close}>
                  <div style={{ paddingLeft: '5px' }} onClick={toggleClass}>
                    <a className="btn btn-md btn-border white-bg ml-2" data-popup="delete_popup">
                      <i className="icon-close blueGray f-0 mr-05" />
                      Cancel Meeting
                    </a>
                  </div>
                  <div className="dropdown-menu drop-right drop-down max-h320">
                    <div className="mini-popup">
                      <div className="mini-popup-header">
                        Cancel Meeting
                        <span className="close_mini_popup" onClick={close}>
                          ×
                        </span>
                      </div>
                      <div className="mini-popup-body">
                        <p className="ln mb-2 darkGray">Are you sure you want to cancel the meeting?</p>
                        <a className="btn btn-md red-bg white d-block jcc" onClick={cancelMeetingSession}>
                          Cancel Meeting
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>
            <li className="flex aic">
              <a className="btn btn-md darkGray-bg ml-2" onClick={openMeetingAttendeesDialog}>
                <span
                  style={{
                    backgroundColor: '#1bc3a7',
                    borderRadius: '10px',
                    width: '10px',
                    height: '10px',
                    marginRight: '5px',
                  }}
                />
                Online
              </a>
            </li>
            <li className="flex aic">
              <a className="btn btn-md darkGray-bg ml-2" onClick={openAgendaFormDialog}>
                <i className="icon-briefcase f-0 mr-05"></i>Show Agenda
              </a>
            </li>
            <Support />
            {/* <li>
                <a className="panel-button no-border popup_button" data-popup="sort_widgets_popup">
                  <i className="icon-dots"></i>
                </a>
              </li> */}
            {/* <li className="dropdown">
              <a className="panel-button dropdown-button" data-action="dropdown">
                <i className="icon-bell"></i> <span className="badge"></span>
              </a>
              <ul className="dropdown-menu notification-menu drop-right"></ul>
            </li> */}
          </ul>
        </div>
      </div>
    </div>
  );
};
